import React, { useState, useMemo } from 'react';
import { usePermissions } from '../../hooks/usePermissions';
import { Card } from '../ui/Card';
import { Badge } from '../ui/Badge';
import { Button } from '../ui/Button';
import { Spinner } from '../ui/Spinner';
import { Modal } from '../ui/Modal';
import {
  ShieldCheck,
  Search,
  Download,
  RefreshCw,
  ShieldAlert,
  Clock,
  Filter,
  Activity,
  Layers,
  User,
  ChevronLeft,
  ChevronRight,
  Eye,
  Copy,
  Check,
  Building2,
  Globe,
  Sliders,
  CheckCircle2,
  Info
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import { useQuery } from '@tanstack/react-query';
import { apiWorker } from '../../api/worker';
import { useAdminUsers, useAdminNodes } from '../../hooks/useAdmin';
import { cn } from '../../lib/utils';

export const AdminAuditManager: React.FC = () => {
  const { isSuperAdmin, isSupportAdmin } = usePermissions();
  const [searchQuery, setSearchQuery] = useState('');
  const [actionFilter, setActionFilter] = useState('ALL');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [page, setPage] = useState(0);
  const [selectedLog, setSelectedLog] = useState<any | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const pageSize = 15;

  // Fetch Audit Logs
  const { data: pageData, isLoading, refetch, isFetching } = useQuery({
    queryKey: ['admin', 'audit', 'logs'],
    queryFn: async () => {
      const res = await apiWorker.get('/api/admin/audit/logs');
      return res.data;
    },
    enabled: isSuperAdmin || isSupportAdmin
  });

  // Prefetch admin users and nodes for real-time human-readable ID resolution
  const { data: usersData } = useAdminUsers(0, 100);
  const { data: nodesData } = useAdminNodes(0, 100);

  const usersList = useMemo(() => {
    return Array.isArray(usersData) ? usersData : (Array.isArray(usersData?.content) ? usersData.content : []);
  }, [usersData]);

  const nodesList = useMemo(() => {
    return Array.isArray(nodesData) ? nodesData : (Array.isArray(nodesData?.content) ? nodesData.content : []);
  }, [nodesData]);

  // Lookup map for Users (keyed by id, clerkId, and email)
  const userMap = useMemo(() => {
    const map = new Map<string, { name: string; email?: string; role?: string }>();
    usersList.forEach((u: any) => {
      const first = u.firstName ? u.firstName.trim() : '';
      const last = u.lastName ? u.lastName.trim() : '';
      const full = (first + ' ' + last).trim();
      const displayName = full || u.username || u.email || u.clerkId || 'Unknown User';
      const info = { name: displayName, email: u.email, role: u.systemAdminRole || u.role };
      if (u.id) map.set(u.id, info);
      if (u.clerkId) map.set(u.clerkId, info);
      if (u.email) map.set(u.email, info);
    });
    return map;
  }, [usersList]);

  // Lookup map for Nodes (keyed by id and slug)
  const nodeMap = useMemo(() => {
    const map = new Map<string, { name: string; slug?: string }>();
    nodesList.forEach((n: any) => {
      const info = { name: n.name || n.title || n.slug || 'Unnamed Org', slug: n.slug };
      if (n.id) map.set(n.id, info);
      if (n.slug) map.set(n.slug, info);
    });
    return map;
  }, [nodesList]);

  const rawLogs = useMemo(() => {
    return Array.isArray(pageData) ? pageData : (Array.isArray(pageData?.content) ? pageData.content : []);
  }, [pageData]);

  // Human resolver for Actor
  const getActorInfo = (log: any) => {
    if (log.actorName) {
      return {
        name: log.actorName,
        email: log.actorEmail,
        id: log.userId || log.actor || '',
        isSystem: String(log.actorName).toLowerCase().includes('system')
      };
    }
    const rawId = log.userId || log.actor || log.user || '';
    if (!rawId || rawId === 'SYSTEM' || rawId === 'system_daemon' || rawId === 'system') {
      return { name: 'System Daemon', email: 'system@orgsets.internal', id: 'system', isSystem: true };
    }
    const lookedUp = userMap.get(rawId);
    if (lookedUp) {
      return {
        name: lookedUp.name,
        email: lookedUp.email,
        id: rawId,
        isSystem: false,
        role: lookedUp.role
      };
    }
    if (/^\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}$/.test(rawId)) {
      return { name: `IP: ${rawId}`, email: undefined, id: rawId, isSystem: false, isIp: true };
    }
    // Formatted fallback
    const shortId = rawId.length > 18 ? `${rawId.slice(0, 10)}...${rawId.slice(-4)}` : rawId;
    return {
      name: shortId,
      email: undefined,
      id: rawId,
      isSystem: false
    };
  };

  // Human resolver for Target / Scope
  const getTargetInfo = (log: any) => {
    if (log.targetName) {
      return {
        name: log.targetName,
        slug: log.nodeSlug,
        id: log.targetId || log.nodeId || ''
      };
    }
    const targetId = log.targetId || log.nodeId || log.target || '';
    if (targetId === 'platform-root' || targetId === 'global' || targetId === 'node-root') {
      return { name: 'Platform Root', slug: 'global', id: targetId, isRoot: true };
    }
    if (targetId === 'platform_config') {
      return { name: 'Platform Configuration', id: targetId };
    }
    if (targetId === 'mongodb_indexes') {
      return { name: 'MongoDB System Indexes', id: targetId };
    }
    const lookedUpNode = nodeMap.get(targetId);
    if (lookedUpNode) {
      return {
        name: lookedUpNode.name,
        slug: lookedUpNode.slug,
        id: targetId,
        isNode: true
      };
    }
    const lookedUpUser = userMap.get(targetId);
    if (lookedUpUser) {
      return {
        name: lookedUpUser.name,
        email: lookedUpUser.email,
        id: targetId,
        isUser: true
      };
    }
    const shortId = targetId.length > 18 ? `${targetId.slice(0, 8)}...${targetId.slice(-4)}` : targetId;
    return {
      name: shortId,
      id: targetId
    };
  };

  // Friendly action label
  const getActionLabel = (action: string) => {
    const map: Record<string, string> = {
      MODULES_UPDATED: 'Modules Updated',
      MEMBER_ROLE_CHANGE: 'Role Changed',
      ROLE_UPDATED: 'Role Updated',
      IMPERSONATE_USER: 'User Impersonated',
      NODE_CREATED: 'Organization Created',
      MEMBER_JOINED: 'Member Joined',
      MEMBER_REMOVED: 'Member Removed',
      SECURITY_BLOCK_IP: 'IP Address Blocked',
      DATABASE_INDEX_SYNC: 'Database Index Sync',
      SMS_CREDITS_ADDED: 'SMS Credits Added',
      VISIBILITY_UPDATED: 'Visibility Updated',
      SYSTEM_SETTINGS_UPDATE: 'System Settings Updated',
      CONFIG_UPDATED: 'Config Updated',
      BRANDING_UPDATED: 'Branding Updated',
      ELECTION_CREATED: 'Election Created',
      VOTE_CAST: 'Vote Cast',
      TRANSACTION_RECORDED: 'Transaction Recorded',
      VAULT_ASSET_ADDED: 'Vault File Added',
      VAULT_ASSET_DELETED: 'Vault File Deleted',
      REQUIREMENT_CREATED: 'Requirement Created',
      EVIDENCE_SUBMITTED: 'Evidence Submitted',
      EVIDENCE_VERIFIED: 'Evidence Verified',
      NEWSLETTER_PUBLISHED: 'Newsletter Published',
      TICKET_CREATED: 'Support Ticket Created',
      TICKET_STATUS_UPDATED: 'Ticket Status Updated'
    };
    return map[action] || action.replace(/_/g, ' ');
  };

  const getActionBadgeVariant = (action: string) => {
    if (action.includes('SECURITY') || action.includes('BLOCK') || action.includes('DELETE') || action.includes('PURGE')) {
      return 'red';
    }
    if (action.includes('IMPERSONATE') || action.includes('ROLE')) {
      return 'purple';
    }
    if (action.includes('MODULES') || action.includes('SETTINGS') || action.includes('CONFIG')) {
      return 'amber';
    }
    if (action.includes('NODE') || action.includes('SYNC')) {
      return 'blue';
    }
    return 'green';
  };

  // Copy helper
  const handleCopy = (text: string, label: string = 'ID') => {
    navigator.clipboard.writeText(text);
    setCopiedId(text);
    toast.success(`Copied ${label} to clipboard`);
    setTimeout(() => setCopiedId(null), 2000);
  };

  // Extract unique actions for dynamic filter dropdown
  const uniqueActions = useMemo(() => {
    const set = new Set<string>();
    rawLogs.forEach((l: any) => {
      if (l.action) set.add(l.action);
    });
    return Array.from(set).sort();
  }, [rawLogs]);

  // Filter logs by search query, action, and status
  const filteredLogs = useMemo(() => {
    return rawLogs.filter((log: any) => {
      const actorInfo = getActorInfo(log);
      const targetInfo = getTargetInfo(log);
      const action = log.action || '';
      const actionLabel = getActionLabel(action);
      const status = log.status || 'SUCCESS';

      if (actionFilter !== 'ALL' && action !== actionFilter) return false;
      if (statusFilter !== 'ALL' && status !== statusFilter) return false;

      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase();
        const matchesAction = action.toLowerCase().includes(q) || actionLabel.toLowerCase().includes(q);
        const matchesActor =
          actorInfo.name.toLowerCase().includes(q) ||
          (actorInfo.email && actorInfo.email.toLowerCase().includes(q)) ||
          actorInfo.id.toLowerCase().includes(q);
        const matchesTarget =
          targetInfo.name.toLowerCase().includes(q) ||
          (targetInfo.slug && targetInfo.slug.toLowerCase().includes(q)) ||
          targetInfo.id.toLowerCase().includes(q);
        const matchesMeta = log.metadata ? JSON.stringify(log.metadata).toLowerCase().includes(q) : false;

        if (!matchesAction && !matchesActor && !matchesTarget && !matchesMeta) {
          return false;
        }
      }

      return true;
    });
  }, [rawLogs, actionFilter, statusFilter, searchQuery, userMap, nodeMap]);

  // Client-side pagination
  const totalPages = Math.ceil(filteredLogs.length / pageSize) || 1;
  const paginatedLogs = useMemo(() => {
    const start = page * pageSize;
    return filteredLogs.slice(start, start + pageSize);
  }, [filteredLogs, page, pageSize]);

  // Metrics
  const securityBlockedCount = useMemo(() => {
    return rawLogs.filter((l: any) => l.status === 'BLOCKED' || String(l.action).includes('SECURITY')).length;
  }, [rawLogs]);

  const userActionCount = useMemo(() => {
    return rawLogs.filter((l: any) => String(l.action).includes('USER') || String(l.action).includes('MEMBER')).length;
  }, [rawLogs]);

  const nodeActionCount = useMemo(() => {
    return rawLogs.filter((l: any) => String(l.action).includes('NODE') || String(l.action).includes('MODULES')).length;
  }, [rawLogs]);

  // Readable CSV Export
  const handleExportCsv = () => {
    if (filteredLogs.length === 0) {
      toast.error('No logs available matching current criteria to export.');
      return;
    }
    const headers = [
      'Timestamp',
      'Actor Name',
      'Actor Email',
      'Actor ID',
      'Action',
      'Target Name',
      'Target Slug',
      'Target ID',
      'Status',
      'Metadata Summary'
    ];
    const rows = filteredLogs.map((l: any) => {
      const actor = getActorInfo(l);
      const target = getTargetInfo(l);
      return [
        `"${l.timestamp || l.time || ''}"`,
        `"${actor.name.replace(/"/g, '""')}"`,
        `"${(actor.email || '').replace(/"/g, '""')}"`,
        `"${actor.id}"`,
        `"${getActionLabel(l.action || '')}"`,
        `"${target.name.replace(/"/g, '""')}"`,
        `"${target.slug || ''}"`,
        `"${target.id}"`,
        `"${l.status || 'SUCCESS'}"`,
        `"${l.metadata ? JSON.stringify(l.metadata).replace(/"/g, '""') : ''}"`
      ];
    });
    const csvContent = [headers.join(','), ...rows.map((r: any) => r.join(','))].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `system-audit-trail-${new Date().toISOString().slice(0, 10)}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    toast.success(`Exported ${filteredLogs.length} audit records to CSV`);
  };

  // Structured human-friendly metadata renderer
  const renderMetadataSummary = (log: any) => {
    if (!log.metadata) {
      return <span className="text-gray-400 dark:text-gray-600">—</span>;
    }
    const meta = log.metadata;

    // Special handler for modules updated
    if (log.action === 'MODULES_UPDATED' || meta.modules || meta.enabled) {
      const enabledList: string[] = [];
      const disabledList: string[] = [];

      if (meta.modules && typeof meta.modules === 'object') {
        Object.entries(meta.modules).forEach(([k, v]) => {
          if (v === true) enabledList.push(k);
          else if (v === false) disabledList.push(k);
        });
      }
      if (Array.isArray(meta.enabled)) {
        enabledList.push(...meta.enabled);
      }
      if (Array.isArray(meta.disabled)) {
        disabledList.push(...meta.disabled);
      }

      return (
        <div className="flex items-center gap-1.5 flex-wrap">
          {enabledList.slice(0, 3).map((mod) => (
            <span
              key={mod}
              className="px-1.5 py-0.5 bg-emerald-100 dark:bg-emerald-950/80 text-emerald-800 dark:text-emerald-300 font-mono text-[10px] font-bold border border-emerald-300 dark:border-emerald-800"
            >
              +{mod}
            </span>
          ))}
          {disabledList.slice(0, 2).map((mod) => (
            <span
              key={mod}
              className="px-1.5 py-0.5 bg-gray-100 dark:bg-slate-800 text-gray-500 dark:text-gray-400 font-mono text-[10px] border border-gray-300 dark:border-slate-700 line-through"
            >
              -{mod}
            </span>
          ))}
          {(enabledList.length > 3 || disabledList.length > 2) && (
            <span className="text-[10px] font-mono text-gray-500 font-bold">
              +{enabledList.length + disabledList.length - 5} more
            </span>
          )}
        </div>
      );
    }

    if (log.action === 'MEMBER_ROLE_CHANGE' || meta.role) {
      return (
        <span className="text-gray-900 dark:text-gray-100 font-medium">
          Role set to <strong className="font-bold text-[#0044FF] dark:text-[#FFD600]">{meta.role}</strong>
        </span>
      );
    }

    if (meta.reason) {
      return (
        <span className="text-gray-700 dark:text-gray-300 italic truncate block max-w-xs" title={meta.reason}>
          "{meta.reason}"
        </span>
      );
    }

    if (meta.field && meta.changedTo !== undefined) {
      return (
        <span className="font-mono text-xs text-gray-700 dark:text-gray-300">
          <strong>{meta.field}</strong> → <span className="text-[#0044FF] dark:text-[#FFD600] font-bold">{String(meta.changedTo)}</span>
        </span>
      );
    }

    if (meta.creditsAdded) {
      return (
        <span className="text-emerald-700 dark:text-emerald-400 font-bold font-mono">
          +{meta.creditsAdded} credits (Total: {meta.newTotal ?? '—'})
        </span>
      );
    }

    if (meta.visibility) {
      return (
        <span className="font-mono text-xs text-gray-700 dark:text-gray-300">
          Visibility: <strong>{meta.visibility}</strong>
        </span>
      );
    }

    // Generic key-value chips
    const entries = Object.entries(meta).filter(([k]) => !['ipAddress', 'timestamp', 'actor'].includes(k));
    if (entries.length === 0) return <span className="text-gray-400">—</span>;

    return (
      <div className="flex items-center gap-1.5 flex-wrap">
        {entries.slice(0, 3).map(([k, v]) => (
          <span
            key={k}
            className="px-1.5 py-0.5 bg-gray-100 dark:bg-slate-800 text-gray-700 dark:text-gray-300 font-mono text-[10px] border border-gray-200 dark:border-slate-700"
          >
            <span className="text-gray-400">{k}:</span> {typeof v === 'object' ? JSON.stringify(v) : String(v)}
          </span>
        ))}
      </div>
    );
  };

  return (
    <div className="space-y-6 animate-in fade-in duration-300">
      {/* Header & Controls */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold tracking-tight text-gray-900 dark:text-white flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-[#0044FF]" />
            Audit Trail & Security Logs
          </h2>
          <p className="text-xs text-gray-500 mt-1">
            Immutable system action ledger, administrative overrides, and platform access monitoring.
          </p>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative w-full sm:w-72">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setPage(0);
              }}
              placeholder="Search by user, org, action, ID..."
              className="w-full pl-9 pr-3 py-2 text-xs bg-white dark:bg-black border-2 border-black dark:border-white rounded-none outline-none focus:ring-2 focus:ring-[#0044FF] text-black dark:text-white font-mono"
            />
          </div>
          <Button
            variant="secondary"
            size="sm"
            onClick={handleExportCsv}
            className="border-2 border-black dark:border-white rounded-none shadow-[2px_2px_0px_#000000] dark:shadow-[2px_2px_0px_#FFFFFF] gap-1.5 text-xs font-bold uppercase tracking-wider"
          >
            <Download size={13} />
            Export CSV
          </Button>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => refetch()}
            className="border-2 border-black dark:border-white rounded-none shadow-[2px_2px_0px_#000000] dark:shadow-[2px_2px_0px_#FFFFFF] gap-1.5 text-xs font-bold uppercase tracking-wider"
            disabled={isFetching}
          >
            <RefreshCw size={13} className={cn(isFetching ? "animate-spin" : "")} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Bauhaus Metric Summary Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <Card className="p-4 rounded-none border-2 border-black dark:border-white shadow-[4px_4px_0px_#000000] dark:shadow-[4px_4px_0px_#FFFFFF] border-t-4 border-t-blue-600 bg-white dark:bg-slate-900">
          <div className="flex items-center justify-between">
            <span className="text-[10px] font-bold uppercase tracking-widest text-gray-500 font-mono">Total Recorded Events</span>
            <Activity className="w-4 h-4 text-blue-500" />
          </div>
          <div className="text-2xl font-black font-display mt-2">{rawLogs.length}</div>
        </Card>

        <Card className="p-4 rounded-none border-2 border-black dark:border-white shadow-[4px_4px_0px_#000000] dark:shadow-[4px_4px_0px_#FFFFFF] border-t-4 border-t-purple-600 bg-white dark:bg-slate-900">
          <div className="flex items-center justify-between">
            <span className="text-[10px] font-bold uppercase tracking-widest text-gray-500 font-mono">User / Identity Actions</span>
            <User className="w-4 h-4 text-purple-500" />
          </div>
          <div className="text-2xl font-black font-display mt-2">{userActionCount}</div>
        </Card>

        <Card className="p-4 rounded-none border-2 border-black dark:border-white shadow-[4px_4px_0px_#000000] dark:shadow-[4px_4px_0px_#FFFFFF] border-t-4 border-t-[#FFD600] bg-white dark:bg-slate-900">
          <div className="flex items-center justify-between">
            <span className="text-[10px] font-bold uppercase tracking-widest text-gray-500 font-mono">Org & Module Ops</span>
            <Layers className="w-4 h-4 text-yellow-600" />
          </div>
          <div className="text-2xl font-black font-display mt-2">{nodeActionCount}</div>
        </Card>

        <Card className="p-4 rounded-none border-2 border-black dark:border-white shadow-[4px_4px_0px_#000000] dark:shadow-[4px_4px_0px_#FFFFFF] border-t-4 border-t-red-600 bg-white dark:bg-slate-900">
          <div className="flex items-center justify-between">
            <span className="text-[10px] font-bold uppercase tracking-widest text-gray-500 font-mono">Blocked Interceptions</span>
            <ShieldAlert className="w-4 h-4 text-red-500" />
          </div>
          <div className="text-2xl font-black font-display mt-2">{securityBlockedCount}</div>
        </Card>
      </div>

      {/* Filter Bar */}
      <div className="flex flex-wrap items-center justify-between gap-3 p-3 bg-white dark:bg-slate-900 border-2 border-black dark:border-white shadow-[2px_2px_0px_#000000] dark:shadow-[2px_2px_0px_#FFFFFF]">
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-1.5 text-xs font-mono font-bold uppercase text-gray-600 dark:text-gray-400">
            <Filter size={13} />
            Filters:
          </div>

          <div className="flex items-center gap-1">
            <label className="text-[11px] font-mono text-gray-500 uppercase">Action Type:</label>
            <select
              value={actionFilter}
              onChange={(e) => {
                setActionFilter(e.target.value);
                setPage(0);
              }}
              className="text-xs font-mono font-bold border border-black dark:border-white bg-white dark:bg-slate-950 px-2 py-1 rounded-none outline-none cursor-pointer"
            >
              <option value="ALL">All Actions ({rawLogs.length})</option>
              {uniqueActions.map((act) => (
                <option key={act} value={act}>{getActionLabel(act)}</option>
              ))}
            </select>
          </div>

          <div className="flex items-center gap-1">
            <label className="text-[11px] font-mono text-gray-500 uppercase">Status:</label>
            <select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setPage(0);
              }}
              className="text-xs font-mono font-bold border border-black dark:border-white bg-white dark:bg-slate-950 px-2 py-1 rounded-none outline-none cursor-pointer"
            >
              <option value="ALL">All Statuses</option>
              <option value="SUCCESS">Success Only</option>
              <option value="BLOCKED">Blocked Only</option>
            </select>
          </div>
        </div>

        {(actionFilter !== 'ALL' || statusFilter !== 'ALL' || searchQuery.trim()) && (
          <button
            type="button"
            onClick={() => {
              setActionFilter('ALL');
              setStatusFilter('ALL');
              setSearchQuery('');
              setPage(0);
            }}
            className="text-xs font-mono font-bold text-red-600 dark:text-red-400 hover:underline uppercase"
          >
            Reset Filters
          </button>
        )}
      </div>

      {/* Main Table */}
      <Card className="p-0 rounded-none border-2 border-black dark:border-white shadow-[4px_4px_0px_#000000] dark:shadow-[4px_4px_0px_#FFFFFF] overflow-hidden bg-white dark:bg-slate-900">
        {isLoading ? (
          <div className="flex flex-col items-center justify-center py-20">
            <Spinner size="lg" />
            <p className="mt-4 text-xs font-mono uppercase tracking-widest text-gray-500">Loading Audit Trail...</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-left font-sans">
              <thead className="bg-black text-white dark:bg-white dark:text-black uppercase font-mono text-[10px] tracking-wider border-b-2 border-black dark:border-white">
                <tr>
                  <th className="px-5 py-3">Timestamp</th>
                  <th className="px-5 py-3">Actor / Initiator</th>
                  <th className="px-5 py-3">Action</th>
                  <th className="px-5 py-3">Target / Scope</th>
                  <th className="px-5 py-3">Context & Changes</th>
                  <th className="px-5 py-3 text-right">Status / Details</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-800 text-xs">
                {paginatedLogs.map((log: any, idx: number) => {
                  const actor = getActorInfo(log);
                  const target = getTargetInfo(log);
                  const isBlocked = log.status === 'BLOCKED';
                  const actionLabel = getActionLabel(log.action || '');

                  return (
                    <tr
                      key={log.id || idx}
                      className={cn(
                        "hover:bg-blue-50/50 dark:hover:bg-slate-800/50 transition-colors cursor-pointer group",
                        isBlocked ? "bg-red-50/40 dark:bg-red-950/20" : ""
                      )}
                      onClick={() => setSelectedLog(log)}
                    >
                      {/* Timestamp */}
                      <td className="px-5 py-3.5 whitespace-nowrap text-gray-600 dark:text-gray-400">
                        <div className="flex items-center gap-1.5 font-mono text-[11px]">
                          <Clock size={12} className="text-gray-400 shrink-0" />
                          <span>{log.timestamp ? new Date(log.timestamp).toLocaleString() : log.time || 'N/A'}</span>
                        </div>
                      </td>

                      {/* Actor (Human-Readable) */}
                      <td className="px-5 py-3.5 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-start gap-2">
                          <div className={cn(
                            "w-7 h-7 rounded-none border border-black dark:border-white flex items-center justify-center shrink-0 mt-0.5",
                            actor.isSystem 
                              ? "bg-purple-100 text-purple-900 dark:bg-purple-950 dark:text-purple-300" 
                              : "bg-[#FFD600] text-black"
                          )}>
                            <User size={13} />
                          </div>
                          <div>
                            <div className="font-bold text-gray-900 dark:text-white font-sans flex items-center gap-1.5">
                              <span>{actor.name}</span>
                              {actor.role && (
                                <span className="text-[9px] px-1 py-0.2 bg-black text-white dark:bg-white dark:text-black font-mono font-bold uppercase">
                                  {actor.role}
                                </span>
                              )}
                            </div>
                            <div className="flex items-center gap-1.5 mt-0.5">
                              {actor.email ? (
                                <span className="text-[11px] text-gray-500 font-sans">{actor.email}</span>
                              ) : (
                                <span className="text-[10px] text-gray-400 font-mono">
                                  {actor.id.length > 20 ? `${actor.id.slice(0, 12)}...` : actor.id}
                                </span>
                              )}
                              {actor.id && actor.id !== 'system' && (
                                <button
                                  type="button"
                                  onClick={() => handleCopy(actor.id, 'User ID')}
                                  title={`Copy ID: ${actor.id}`}
                                  className="text-gray-400 hover:text-black dark:hover:text-white transition-colors"
                                >
                                  {copiedId === actor.id ? <Check size={11} className="text-emerald-500" /> : <Copy size={11} />}
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      </td>

                      {/* Action */}
                      <td className="px-5 py-3.5 whitespace-nowrap">
                        <Badge
                          variant={getActionBadgeVariant(log.action || '')}
                          className="rounded-none border border-black dark:border-white font-mono uppercase text-[10px] font-bold tracking-wider"
                        >
                          {actionLabel}
                        </Badge>
                      </td>

                      {/* Target / Scope (Human-Readable) */}
                      <td className="px-5 py-3.5 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-start gap-1.5">
                          <Building2 size={13} className="text-gray-400 shrink-0 mt-0.5" />
                          <div>
                            <span className="font-bold text-gray-900 dark:text-white block font-sans">
                              {target.name}
                            </span>
                            <div className="flex items-center gap-1.5 mt-0.5">
                              {target.slug ? (
                                <span className="inline-block px-1.5 py-0.2 bg-blue-50 dark:bg-blue-950/80 text-[#0044FF] dark:text-blue-300 font-mono text-[10px] font-bold border border-blue-200 dark:border-blue-800">
                                  /{target.slug}
                                </span>
                              ) : (
                                <span className="text-[10px] text-gray-400 font-mono">
                                  {target.id.length > 18 ? `${target.id.slice(0, 10)}...` : target.id}
                                </span>
                              )}
                              {target.id && (
                                <button
                                  type="button"
                                  onClick={() => handleCopy(target.id, 'Target ID')}
                                  title={`Copy ID: ${target.id}`}
                                  className="text-gray-400 hover:text-black dark:hover:text-white transition-colors"
                                >
                                  {copiedId === target.id ? <Check size={11} className="text-emerald-500" /> : <Copy size={11} />}
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      </td>

                      {/* Metadata Summary */}
                      <td className="px-5 py-3.5 max-w-sm">
                        {renderMetadataSummary(log)}
                      </td>

                      {/* Status / Inspect Action */}
                      <td className="px-5 py-3.5 text-right whitespace-nowrap">
                        <div className="flex items-center justify-end gap-2">
                          {isBlocked ? (
                            <Badge variant="red" className="rounded-none border border-red-700 font-mono text-[10px] font-bold uppercase tracking-wider">
                              <ShieldAlert size={11} className="mr-1 inline" /> BLOCKED
                            </Badge>
                          ) : (
                            <Badge variant="green" className="rounded-none border border-green-700 font-mono text-[10px] font-bold uppercase tracking-wider">
                              {log.status || 'SUCCESS'}
                            </Badge>
                          )}
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedLog(log);
                            }}
                            className="p-1.5 border border-black dark:border-white rounded-none shadow-[2px_2px_0px_#000000] dark:shadow-[2px_2px_0px_#FFFFFF] hover:bg-neutral-100"
                            title="Inspect full audit record"
                          >
                            <Eye size={12} />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}

                {filteredLogs.length === 0 && (
                  <tr>
                    <td colSpan={6} className="py-16 text-center text-gray-500">
                      <div className="flex flex-col items-center justify-center">
                        <ShieldCheck size={36} className="text-gray-300 dark:text-gray-700 mb-2" />
                        <span className="font-display font-bold uppercase tracking-wider text-sm">No Audit Logs Found</span>
                        <span className="text-xs font-sans text-gray-400 mt-1">No event entries matched your search query or filter selection.</span>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination Footer */}
        {filteredLogs.length > pageSize && (
          <div className="flex flex-col sm:flex-row items-center justify-between p-4 border-t-2 border-black dark:border-white bg-gray-50 dark:bg-slate-950 gap-3">
            <span className="text-xs font-mono text-gray-500">
              Showing <span className="font-bold text-gray-900 dark:text-white">{page * pageSize + 1}</span> to <span className="font-bold text-gray-900 dark:text-white">{Math.min((page + 1) * pageSize, filteredLogs.length)}</span> of <span className="font-bold text-gray-900 dark:text-white">{filteredLogs.length}</span> logs
            </span>

            <div className="flex items-center gap-2">
              <Button
                variant="secondary"
                size="sm"
                disabled={page === 0}
                onClick={() => setPage(p => Math.max(0, p - 1))}
                className="text-xs font-mono font-bold uppercase rounded-none border-2 border-black dark:border-white shadow-[2px_2px_0px_#000000] dark:shadow-[2px_2px_0px_#FFFFFF] gap-1"
              >
                <ChevronLeft size={14} /> Prev
              </Button>
              <span className="text-xs font-mono px-2 font-bold">
                {page + 1} / {totalPages}
              </span>
              <Button
                variant="secondary"
                size="sm"
                disabled={page >= totalPages - 1}
                onClick={() => setPage(p => p + 1)}
                className="text-xs font-mono font-bold uppercase rounded-none border-2 border-black dark:border-white shadow-[2px_2px_0px_#000000] dark:shadow-[2px_2px_0px_#FFFFFF] gap-1"
              >
                Next <ChevronRight size={14} />
              </Button>
            </div>
          </div>
        )}
      </Card>

      {/* Detailed Inspection Modal */}
      {selectedLog && (
        <Modal
          isOpen={!!selectedLog}
          onClose={() => setSelectedLog(null)}
          title={`Audit Record Inspector • ${getActionLabel(selectedLog.action || '')}`}
          className="max-w-2xl"
          footer={
            <div className="flex items-center justify-between w-full">
              <span className="font-mono text-xs text-gray-400">
                Log ID: {selectedLog.id || 'N/A'}
              </span>
              <div className="flex items-center gap-2">
                {selectedLog.metadata && (
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => handleCopy(JSON.stringify(selectedLog.metadata, null, 2), 'Metadata JSON')}
                    className="border-2 border-black dark:border-white rounded-none shadow-[2px_2px_0px_#000000] dark:shadow-[2px_2px_0px_#FFFFFF] text-xs font-bold uppercase"
                  >
                    <Copy size={13} className="mr-1 inline" /> Copy JSON
                  </Button>
                )}
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => setSelectedLog(null)}
                  className="bg-black text-white dark:bg-white dark:text-black border-2 border-black dark:border-white rounded-none text-xs font-bold uppercase"
                >
                  Close Inspector
                </Button>
              </div>
            </div>
          }
        >
          <div className="space-y-5">
            {/* Top Overview Strip */}
            <div className="p-3 bg-neutral-50 dark:bg-slate-900 border-2 border-black dark:border-white flex flex-wrap items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <Badge variant={getActionBadgeVariant(selectedLog.action || '')} className="rounded-none border border-black dark:border-white font-mono font-bold">
                  {getActionLabel(selectedLog.action || '')}
                </Badge>
                <Badge variant={selectedLog.status === 'BLOCKED' ? 'red' : 'green'} className="rounded-none border border-black dark:border-white font-mono font-bold">
                  {selectedLog.status || 'SUCCESS'}
                </Badge>
              </div>
              <div className="text-xs font-mono text-gray-600 dark:text-gray-300 flex items-center gap-1.5">
                <Clock size={13} />
                <span>{selectedLog.timestamp ? new Date(selectedLog.timestamp).toUTCString() : 'N/A'}</span>
              </div>
            </div>

            {/* Actor Card */}
            {(() => {
              const actor = getActorInfo(selectedLog);
              return (
                <div className="p-4 border-2 border-black dark:border-white bg-white dark:bg-slate-950 space-y-2">
                  <span className="font-mono text-xs font-bold uppercase text-gray-500 tracking-wider block">
                    Actor / Initiator
                  </span>
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="text-base font-bold text-gray-900 dark:text-white font-sans">{actor.name}</h4>
                      {actor.email && (
                        <p className="text-xs text-gray-500 font-sans mt-0.5">{actor.email}</p>
                      )}
                    </div>
                    {actor.role && (
                      <span className="px-2 py-0.5 bg-[#FFD600] text-black font-mono font-bold text-xs border border-black">
                        {actor.role}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center justify-between pt-2 border-t border-gray-100 dark:border-slate-800 text-xs font-mono text-gray-500">
                    <span>Clerk ID: <strong className="text-gray-800 dark:text-gray-200">{actor.id}</strong></span>
                    {actor.id && (
                      <button
                        type="button"
                        onClick={() => handleCopy(actor.id, 'User ID')}
                        className="text-xs underline hover:text-[#0044FF]"
                      >
                        Copy
                      </button>
                    )}
                  </div>
                </div>
              );
            })()}

            {/* Target & Scope Card */}
            {(() => {
              const target = getTargetInfo(selectedLog);
              return (
                <div className="p-4 border-2 border-black dark:border-white bg-white dark:bg-slate-950 space-y-2">
                  <span className="font-mono text-xs font-bold uppercase text-gray-500 tracking-wider block">
                    Target Organization / Scope
                  </span>
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="text-base font-bold text-gray-900 dark:text-white font-sans">{target.name}</h4>
                      {target.slug && (
                        <p className="text-xs text-[#0044FF] dark:text-[#FFD600] font-mono mt-0.5">
                          orgsets.com/{target.slug}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center justify-between pt-2 border-t border-gray-100 dark:border-slate-800 text-xs font-mono text-gray-500">
                    <span>Target ID: <strong className="text-gray-800 dark:text-gray-200">{target.id}</strong></span>
                    {target.id && (
                      <button
                        type="button"
                        onClick={() => handleCopy(target.id, 'Target ID')}
                        className="text-xs underline hover:text-[#0044FF]"
                      >
                        Copy
                      </button>
                    )}
                  </div>
                </div>
              );
            })()}

            {/* Metadata Payload Card */}
            <div className="p-4 border-2 border-black dark:border-white bg-white dark:bg-slate-950 space-y-2">
              <span className="font-mono text-xs font-bold uppercase text-gray-500 tracking-wider block">
                Structured Metadata Context
              </span>
              {selectedLog.metadata ? (
                <pre className="p-3 bg-neutral-900 text-emerald-400 font-mono text-xs overflow-x-auto max-h-60 border border-black dark:border-white rounded-none">
                  {JSON.stringify(selectedLog.metadata, null, 2)}
                </pre>
              ) : (
                <p className="text-xs text-gray-400 italic">No additional metadata was recorded for this event.</p>
              )}
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};
